import React, { useState, useEffect, useCallback } from "react";
import { Row, Col, Card, Select, Spin, Alert, Button, Space, Typography } from "antd";
import { Line, Pie, Bar } from "@ant-design/plots";
import dayjs from "dayjs";
import { fetchAnalytics } from "../../services/aiService";

const NVIDIA_GREEN = "#76B900";

const RANGE_PRESETS = [
  { value: "30d",  label: "Last 30 days",  hoursBack: 24 * 30 },
  { value: "90d",  label: "Last 90 days",  hoursBack: 24 * 90 },
  { value: "mtd",  label: "Month to date", monthToDate: true },
];

const QUICK_QUESTIONS = [
  "How many registrations failed in the last 7 days?",
  "Show the top 5 countries by registration count this month.",
  "Monthly registration trend for the past year.",
  "Which product family has the most failed registrations in the last 30 days?",
];

// One chart card with its own loading/error state. Keeps each chart isolated
// so one failing query doesn't blank the whole tab.
function ChartCard({ title, loading, error, empty, children }) {
  return (
    <Card size="small" title={title} style={{ height: 320 }} bodyStyle={{ height: 260, padding: 12 }}>
      {loading && <div style={{ textAlign: "center", paddingTop: 80 }}><Spin /></div>}
      {!loading && error && <Alert type="error" message={error} showIcon />}
      {!loading && !error && empty && (
        <Typography.Text type="secondary" style={{ display: "block", textAlign: "center", paddingTop: 80 }}>
          No data for this range.
        </Typography.Text>
      )}
      {!loading && !error && !empty && children}
    </Card>
  );
}

export default function AnalyticsTab({ onAskQuestion }) {
  const [range, setRange] = useState("30d");

  const [monthly, setMonthly] = useState({ loading: true, error: null, data: [] });
  const [status,  setStatus]  = useState({ loading: true, error: null, data: [] });
  const [family,  setFamily]  = useState({ loading: true, error: null, data: [] });

  // Monthly trend always covers the last 12 calendar months regardless of the
  // range preset — that's the point of a trend view.
  const loadMonthly = useCallback(async () => {
    setMonthly({ loading: true, error: null, data: [] });
    try {
      const startDate = dayjs().subtract(11, "month").startOf("month").format("YYYY-MM-DD");
      const endDate   = dayjs().add(1, "day").format("YYYY-MM-DD");
      const r = await fetchAnalytics({ startDate, endDate, groupBy: "month" });
      const data = (r.breakdown || []).map(b => ({ month: b.groupKey, count: b.count }));
      data.sort((a, b) => a.month.localeCompare(b.month));
      setMonthly({ loading: false, error: null, data });
    } catch (e) {
      setMonthly({ loading: false, error: e.message || "Failed to load monthly trend", data: [] });
    }
  }, []);

  // The two range-sensitive charts share a date window picked from the preset.
  const loadRangeCharts = useCallback(async () => {
    setStatus({ loading: true, error: null, data: [] });
    setFamily({ loading: true, error: null, data: [] });

    const preset = RANGE_PRESETS.find(p => p.value === range) || RANGE_PRESETS[0];
    const params = preset.monthToDate
      ? { startDate: dayjs().startOf("month").format("YYYY-MM-DD") }
      : { hoursBack: preset.hoursBack };

    try {
      const r = await fetchAnalytics({ ...params, groupBy: "status" });
      const data = (r.breakdown || []).map(b => ({ status: b.groupKey || "(unknown)", count: b.count }));
      setStatus({ loading: false, error: null, data });
    } catch (e) {
      setStatus({ loading: false, error: e.message || "Failed to load status breakdown", data: [] });
    }

    try {
      const r = await fetchAnalytics({ ...params, groupBy: "productfamily" });
      const data = (r.breakdown || []).slice(0, 8).map(b => ({
        family: b.groupKey || "(unknown)",
        count:  b.count,
      }));
      setFamily({ loading: false, error: null, data });
    } catch (e) {
      setFamily({ loading: false, error: e.message || "Failed to load product families", data: [] });
    }
  }, [range]);

  useEffect(() => { loadMonthly(); }, [loadMonthly]);
  useEffect(() => { loadRangeCharts(); }, [loadRangeCharts]);

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
        <Typography.Text strong>Date range (affects status + product family):</Typography.Text>
        <Select
          value={range}
          onChange={setRange}
          options={RANGE_PRESETS.map(p => ({ value: p.value, label: p.label }))}
          style={{ width: 180 }}
        />
      </div>

      <Row gutter={[12, 12]}>
        <Col xs={24} md={12} lg={8}>
          <ChartCard
            title="Monthly trend (last 12 months)"
            loading={monthly.loading}
            error={monthly.error}
            empty={monthly.data.length === 0}
          >
            <Line
              data={monthly.data}
              xField="month"
              yField="count"
              smooth
              color={NVIDIA_GREEN}
              point={{ size: 3, shape: "circle" }}
              xAxis={{ tickCount: 6 }}
              yAxis={{ min: 0 }}
              tooltip={{ showMarkers: true }}
              autoFit
            />
          </ChartCard>
        </Col>

        <Col xs={24} md={12} lg={8}>
          <ChartCard
            title="Status breakdown"
            loading={status.loading}
            error={status.error}
            empty={status.data.length === 0}
          >
            <Pie
              data={status.data}
              angleField="count"
              colorField="status"
              radius={0.9}
              innerRadius={0.6}
              legend={{ position: "right" }}
              label={false}
              autoFit
            />
          </ChartCard>
        </Col>

        <Col xs={24} md={24} lg={8}>
          <ChartCard
            title="Top product families"
            loading={family.loading}
            error={family.error}
            empty={family.data.length === 0}
          >
            <Bar
              data={family.data}
              xField="count"
              yField="family"
              seriesField="family"
              legend={false}
              color={NVIDIA_GREEN}
              barWidthRatio={0.6}
              autoFit
            />
          </ChartCard>
        </Col>
      </Row>

      <div style={{ marginTop: 20, paddingTop: 16, borderTop: "1px solid #f0f0f0" }}>
        <Typography.Text strong style={{ display: "block", marginBottom: 8 }}>
          Quick questions
        </Typography.Text>
        <Typography.Text type="secondary" style={{ display: "block", marginBottom: 12, fontSize: 12 }}>
          Click any question to ask it in the Chat tab.
        </Typography.Text>
        <Space wrap>
          {QUICK_QUESTIONS.map(q => (
            <Button key={q} size="small" onClick={() => onAskQuestion(q)}>
              {q}
            </Button>
          ))}
        </Space>
      </div>
    </div>
  );
}
